// Event audit (GET /api/events/:id/audit): everything recorded about one event, oldest first.
// Shown in the console's Log tab (Log / Audit toggle) and in a drawer from #/events.
// Reading it is itself audited (event.audit_viewed), so it loads on open and on Refresh only.

import { Fragment, useEffect, useId, useRef, useState } from 'react';
import { api, ApiError, type AuditEntry } from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { ErrorBox, JsonBlock, Loading } from '../components/ui.tsx';
import { useLoad } from '../hooks.ts';
import { fmtDateTime, fmtTime, tzAbbr } from '../time.ts';
import { RecordingView } from './Calls.tsx';
import { dur, human } from './util.ts';

function scalar(v: unknown): string | null {
  if (typeof v === 'string') return v.length > 48 ? `${v.slice(0, 46)}…` : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return null;
}
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

/** Keys that are plumbing rather than meaning, left out of the compact summary. */
const HIDDEN = new Set(['text', 'logSeq', 'eventId', 'actionId', 'viewId', 'objectKey', 'dialedE164Hash']);

export interface AuditSummary {
  text: string;
  chips: { label: string; tone?: 'ok' | 'bad' | 'warn' | 'info' }[];
}

/** A one-line reading of an audit entry: its text, or a compact summary of `data`. */
export function summarize(e: AuditEntry): AuditSummary {
  const d = e.data;
  const chips: AuditSummary['chips'] = [];
  const a = e.action;
  if (a.endsWith('watched')) {
    const w = num(d.watchedMs) ?? (num(d.watchedSeconds) !== null ? (num(d.watchedSeconds) ?? 0) * 1000 : null);
    // Rounded to the second like the server's log text ("for 0:04").
    if (w !== null) chips.push({ label: dur(Math.round(w / 1000) * 1000), tone: 'info' });
    const mode = str(d.mode);
    const cam = str(d.cameraName) ?? str(d.cameraId) ?? 'camera';
    const reason = str(d.endReason);
    if (reason && reason !== 'switched' && reason !== 'closed') chips.push({ label: `ended: ${reason}` });
    return { text: e.text ?? `Watched ${mode === 'alarm' ? 'the alarm clip' : (mode ?? 'video')} · ${cam}`, chips };
  }
  if (a.includes('call')) {
    const state = str(d.state);
    if (state) chips.push({ label: state, tone: state === 'connected' ? 'ok' : state === 'failed' ? 'bad' : undefined });
    const talk = num(d.talkSeconds);
    if (talk !== null && talk > 0) chips.push({ label: `talk ${dur(talk * 1000)}` });
    if (a.includes('recording_stored')) chips.push({ label: 'recording stored', tone: 'ok' });
    else if (a.includes('recording_missing')) chips.push({ label: 'recording missing', tone: 'bad' });
    else if (a.includes('recording_played')) chips.push({ label: 'recording played', tone: 'info' });
    const sha = str(d.sha256);
    // recording_stored rows carry a player that shows the hash itself.
    if (sha && !a.includes('recording_stored')) chips.push({ label: `SHA-256 ${sha.slice(0, 12)}` });
    const who = str(d.contactName);
    return { text: e.text ?? `${human(a.replace(/^event\./, ''))}${who ? ` · ${who}` : ''}`, chips };
  }
  if (e.text) return { text: e.text, chips };
  const parts = Object.entries(d)
    .filter(([k]) => !HIDDEN.has(k))
    .map(([k, v]) => {
      const s = scalar(v);
      return s === null ? null : `${k}: ${s}`;
    })
    .filter((x): x is string => x !== null)
    .slice(0, 4);
  return { text: parts.join(' · '), chips };
}

function actorText(e: AuditEntry): string {
  return e.actor.name ?? (e.actor.type === 'system' ? 'System' : e.actor.type === 'agent' ? 'AI agent' : e.actor.type);
}

export function EventAudit({ eventId, tz }: { eventId: string; tz: string }) {
  const load = useLoad((signal) => api.eventAudit(eventId, signal), [eventId]);
  const [open, setOpen] = useState<string | null>(null);
  const rows = load.data ?? [];
  const forbidden = load.error instanceof ApiError && load.error.status === 403;
  return (
    <div className="sec">
      <div className="lbl">
        <span>Event audit · {tzAbbr(tz)} site time</span>
        <button type="button" className="btn ghost sm" onClick={load.reload} disabled={load.loading} title="Reload (reading the audit is itself audited)">
          <Icon name="sync" />
          {load.loading && load.data ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      {load.error && !load.data ? (
        forbidden ? (
          <div className="note">Only the event's owner, supervisors, admins and auditors can read its audit.</div>
        ) : (
          <ErrorBox error={load.error} onRetry={load.reload} title="Could not load the audit" />
        )
      ) : !load.data ? (
        <Loading label="Loading audit…" />
      ) : rows.length === 0 ? (
        <div className="note">Nothing audited for this event yet.</div>
      ) : (
        <div className="log audit">
          {rows.map((e) => {
            const s = summarize(e);
            const isOpen = open === e.id;
            return (
              <Fragment key={e.id}>
                <button type="button" className={`le${isOpen ? ' open' : ''}`} onClick={() => setOpen(isOpen ? null : e.id)} aria-expanded={isOpen} title={fmtDateTime(e.at, tz)}>
                  <span className="mono dim">{fmtTime(e.at, tz)}</span>
                  <span className={`lwho a-${e.actor.type}`} title={`${e.actor.type}${e.actor.id ? ` · ${e.actor.id}` : ''}`}>
                    {actorText(e)}
                  </span>
                  <span className="atext">
                    <span className="mono aact">{e.action}</span>
                    {s.text && <span>{s.text}</span>}
                    {s.chips.map((ch) => (
                      <span key={ch.label} className={`pill tiny-pill${ch.tone ? ` ${ch.tone}` : ''}`}>
                        {ch.label}
                      </span>
                    ))}
                  </span>
                </button>
                {e.action.endsWith('call_recording_stored') && str(e.data.actionId) && (
                  <div className="arec">
                    <RecordingView
                      eventId={eventId}
                      actionId={str(e.data.actionId) ?? ''}
                      rec={{ status: 'stored', durationSeconds: null, sha256: str(e.data.sha256), contentType: null, url: null }}
                    />
                  </div>
                )}
                {isOpen && (
                  <div className="ajson">
                    <JsonBlock value={e.data} maxHeight={260} />
                  </div>
                )}
              </Fragment>
            );
          })}
        </div>
      )}
      {load.data && <div className="note">{rows.length} entries, oldest first. Click an entry for its full data.</div>}
    </div>
  );
}

/** The audit in a drawer (supervisor events list). */
export function AuditDrawer({ eventId, title, tz, onClose }: { eventId: string; title: string; tz: string; onClose: () => void }) {
  const id = useId();
  const closeRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    const prev = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      prev?.focus();
    };
  }, [onClose]);
  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside className="drawer wide" role="dialog" aria-modal="true" aria-labelledby={id}>
        <div className="dh">
          <Icon name="shield" />
          <div className="dh-t">
            <h3 id={id}>Audit · {title}</h3>
            <div className="note">Every action recorded on this event, including calls, recordings and video watch time.</div>
          </div>
          <button ref={closeRef} type="button" className="btn icon" onClick={onClose} aria-label="Close audit">
            <Icon name="x" />
          </button>
        </div>
        <div className="db">
          <EventAudit eventId={eventId} tz={tz} />
        </div>
      </aside>
    </div>
  );
}
